import React, { useState } from 'react';
import { RichTextDescription, RichTextElement } from 'coop-adventure-engine';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Toast from '../components/ui/Toast';
import Header from '../components/layout/Header';
import MobileLayout from '../components/layout/MobileLayout';
import SceneDisplay from '../components/game/SceneDisplay';
import OptionButton from '../components/game/OptionButton';
import PlayerStatus from '../components/game/PlayerStatus';
import InventoryPanel from '../components/game/InventoryPanel';
import WaitingIndicator from '../components/game/WaitingIndicator';

const UITestPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const makeParagraph = (text: string): RichTextElement => ({
    type: 'paragraph',
    content: text
  });

  const conditionalRichTextScene: RichTextDescription = {
    elements: [
      makeParagraph('You find yourself at the edge of a dark forest. Ancient trees tower above you, their branches creating a canopy that blocks most of the sunlight.'),
      {
        type: 'conditional',
        children: [
          makeParagraph('Since you have active adventures, a faint trail of glowing footprints marks your path forward.')
        ],
        elseChildren: [
          makeParagraph('With no active adventures, the forest remains quiet and still, inviting you to begin your journey.')
        ]
      }
    ]
  };

  const conditionalElseOnlyScene: RichTextDescription = {
    elements: [
      {
        type: 'conditional',
        elseChildren: [
          makeParagraph('This is the fallback branch rendered when no conditionally-visible content is available.')
        ]
      }
    ]
  };

  // Mock data for game components
  const mockScene = {
    id: 'test-scene',
    title: 'The Mysterious Forest',
    description: conditionalRichTextScene,
    imageUrl: undefined
  };

  const mockOptions = [
    { id: '1', text: 'Follow the narrow path deeper into the forest', isAvailable: true },
    { id: '2', text: 'Take the wider trail toward the clearing', isAvailable: true },
    { id: '3', text: 'Examine the ancient trees more closely', isAvailable: false, reason: 'You need a magnifying glass' }
  ];

  const mockPlayer = {
    id: 'player1',
    name: 'Test Player',
    isOnline: true,
    inventory: [
      { id: '1', name: 'Rusty Sword', type: 'tool' as const, description: 'An old but serviceable blade' },
      { id: '2', name: 'Health Potion', type: 'consumable' as const, description: 'Restores 50 HP' },
      { id: '3', name: 'Magic Ring', type: 'item' as const, description: 'Grants +5 to all stats' }
    ],
    stats: {
      health: 85,
      maxHealth: 100,
      mana: 60,
      maxMana: 80,
      level: 5,
      experience: 1250,
      nextLevelExp: 2000
    }
  };

  return (
    <>
      <MobileLayout 
        showSidebar={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      >
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">UI Components Test Page</h1>
          
          {/* Layout Components */}
          <section className="bg-white rounded-lg shadow p-4">
            <h2 className="text-xl font-semibold mb-4">Layout Components</h2>
            
            {/* Header */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Header</h3>
              <div className="border rounded-lg overflow-hidden">
                <Header 
                  title="Test Header" 
                  onToggleSidebar={() => setSidebarOpen(true)}
                  showBackButton={false}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Sidebar</h3>
              <p className="text-sm text-gray-600 mb-2">
                Click the menu button in the header above to test the sidebar functionality.
              </p>
              <div className="text-sm text-gray-500">
                <p>• The sidebar should slide in from the left on mobile</p>
                <p>• It should be visible on desktop by default</p>
                <p>• Click the X button or backdrop to close it</p>
              </div>
            </div>
          </section>

          {/* Game Components */}
          <section className="bg-white rounded-lg shadow p-4">
            <h2 className="text-xl font-semibold mb-4">Game Components</h2>
            
            {/* Scene Display */}
            <div className="mb-4 space-y-4">
              <h3 className="text-lg font-medium mb-2">Scene Display</h3>
              <div className="border rounded-lg overflow-hidden">
                <SceneDisplay 
                  title={mockScene.title}
                  description={mockScene.description}
                />
              </div>
              <div className="border rounded-lg overflow-hidden">
                <SceneDisplay
                  title="Conditional Else Branch Example"
                  description={conditionalElseOnlyScene}
                />
              </div>
            </div>

            {/* Option Buttons */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Option Buttons</h3>
              <div className="space-y-2">
                {mockOptions.map(option => (
                  <OptionButton
                    key={option.id}
                    text={option.text}
                    onClick={() => {}}
                    disabled={!option.isAvailable}
                  />
                ))}
              </div>
            </div>

            {/* Player Status */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Player Status</h3>
              <div className="border rounded-lg overflow-hidden">
                <PlayerStatus 
                  playerName={mockPlayer.name}
                  characterName="Adventure Hero"
                  currentScene="The Mysterious Forest"
                  inventory={mockPlayer.inventory.map(item => item.name)}
                  stats={mockPlayer.stats}
                  isOnline={mockPlayer.isOnline}
                />
              </div>
            </div>

            {/* Inventory Panel */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Inventory Panel</h3>
              <div className="border rounded-lg overflow-hidden">
                <InventoryPanel 
                  items={mockPlayer.inventory}
                  onItemUse={() => {}}
                />
              </div>
            </div>

            {/* Waiting Indicator */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Waiting Indicator</h3>
              <div className="space-y-2">
                <WaitingIndicator 
                  message="Waiting for other players..." 
                  isVisible={true}
                />
                <WaitingIndicator 
                  message="Loading adventure..." 
                  isVisible={true}
                  progress={60}
                  showProgress={true}
                />
              </div>
            </div>
          </section>
        
        {/* Core UI Components */}
        <section className="bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Core UI Components</h2>
          
          {/* Buttons */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Buttons</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" size="sm">Primary</Button>
              <Button variant="secondary" size="sm">Secondary</Button>
              <Button variant="danger" size="sm">Danger</Button>
              <Button variant="ghost" size="sm">Ghost</Button>
              <Button loading size="sm">Loading</Button>
              <Button disabled size="sm">Disabled</Button>
            </div>
          </div>

          {/* Input Fields */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Input Fields</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Default Input" placeholder="Enter text..." size="sm" />
              <Input label="Input with Error" error="This field is required" size="sm" />
              <Input label="Input with Helper" helperText="This is helpful text" size="sm" />
              <Input label="Disabled Input" disabled value="Cannot edit" size="sm" />
            </div>
          </div>

          {/* Loading Spinners */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Loading Spinners</h3>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <LoadingSpinner size="xs" />
                <p className="text-xs mt-1">XS</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="sm" />
                <p className="text-xs mt-1">SM</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="md" />
                <p className="text-xs mt-1">MD</p>
              </div>
              <div className="text-center">
                <LoadingSpinner size="lg" />
                <p className="text-xs mt-1">LG</p>
              </div>
            </div>
          </div>

          {/* Modal */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Modal</h3>
            <Button onClick={() => setShowModal(true)} size="sm">Open Modal</Button>
          </div>

          {/* Toast */}
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Toast Notifications</h3>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => { setToastType('success'); setShowToast(true); }} size="sm">
                Success
              </Button>
              <Button onClick={() => { setToastType('error'); setShowToast(true); }} size="sm">
                Error
              </Button>
              <Button onClick={() => { setToastType('warning'); setShowToast(true); }} size="sm">
                Warning
              </Button>
              <Button onClick={() => { setToastType('info'); setShowToast(true); }} size="sm">
                Info
              </Button>
            </div>
          </div>
        </section>
        </div>
      </div>
    </MobileLayout>

    {/* Modal - rendered outside MobileLayout to avoid z-index issues */}
    <Modal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      title="Test Modal"
      size="md"
    >
      <p className="text-gray-600 mb-4">
        This is a test modal. You can close it by clicking the backdrop or the X button.
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => setShowModal(false)} size="sm">
          Cancel
        </Button>
        <Button onClick={() => setShowModal(false)} size="sm">
          Confirm
        </Button>
      </div>
    </Modal>

    {/* Toast - rendered outside MobileLayout */}
    {showToast && (
      <Toast
        id="test-toast"
        type={toastType}
        message={`This is a ${toastType} message!`}
        onClose={() => setShowToast(false)}
        duration={5000}
      />
    )}
  </>
  );
};

export default UITestPage;